import clsx from 'clsx';
import type { TeamLineup } from '../../types';

interface Props {
  awayLineup?: TeamLineup;
  homeLineup?: TeamLineup;
  awayTeamAbbr: string;
  homeTeamAbbr: string;
}

function opsColor(ops: number): string {
  if (ops >= 0.850) return 'text-win';
  if (ops >= 0.750) return 'text-warn';
  if (ops >= 0.660) return 'text-gray-300';
  return 'text-loss';
}

function platoonBar(left: number, right: number, switchBats: number): string {
  const total = left + right + switchBats;
  if (total === 0) return '';
  return `${left}L / ${right}R${switchBats ? ` / ${switchBats}S` : ''}`;
}

function LineupPanel({ lineup, teamAbbr }: { lineup?: TeamLineup; teamAbbr: string }) {
  if (!lineup) {
    return (
      <div className="card p-4 text-center text-gray-600 text-sm">
        {teamAbbr} lineup not yet confirmed
      </div>
    );
  }

  const s = lineup.lineupStrength;

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-100">{teamAbbr}</span>
        <span className="text-xs text-gray-500">
          {platoonBar(
            lineup.platoonComposition.leftBats,
            lineup.platoonComposition.rightBats,
            lineup.platoonComposition.switchBats,
          )}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2 pt-2 border-t border-surface-4">
        <div className="text-center">
          <div className={clsx('text-sm font-mono font-semibold', opsColor(s.avgOPS))}>
            {s.avgOPS.toFixed(3)}
          </div>
          <div className="text-[10px] text-gray-500 uppercase tracking-wide mt-0.5">Avg OPS</div>
        </div>
        <div className="text-center">
          <div className={clsx('text-sm font-mono font-semibold', opsColor(s.heartOPS))}>
            {s.heartOPS.toFixed(3)}
          </div>
          <div className="text-[10px] text-gray-500 uppercase tracking-wide mt-0.5">3-4-5 OPS</div>
        </div>
        {s.avgXwOBA != null && (
          <div className="text-center">
            <div className={clsx('text-sm font-mono font-semibold', s.avgXwOBA >= 0.340 ? 'text-win' : s.avgXwOBA >= 0.310 ? 'text-warn' : 'text-loss')}>
              {s.avgXwOBA.toFixed(3)}
            </div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wide mt-0.5">xwOBA</div>
          </div>
        )}
      </div>

      {/* Top 5 batters */}
      <div className="space-y-0.5 pt-2 border-t border-surface-4">
        {lineup.batters.slice(0, 5).map((b) => (
          <div key={b.id} className="flex items-center gap-2 text-xs">
            <span className="text-gray-600 w-4 text-right font-mono">{b.battingOrder}</span>
            <span className="flex-1 text-gray-300 truncate">{b.fullName}</span>
            <span className={clsx('font-mono text-[11px]', opsColor(b.ops))}>{b.ops.toFixed(3)}</span>
            {b.batSide && (
              <span className="text-[10px] text-gray-600 w-3">{b.batSide}</span>
            )}
          </div>
        ))}
        {lineup.batters.length > 5 && (
          <div className="text-[10px] text-gray-600 pl-6 pt-0.5">
            + {lineup.batters.length - 5} more
          </div>
        )}
      </div>
    </div>
  );
}

export function LineupCorrelation({ awayLineup, homeLineup, awayTeamAbbr, homeTeamAbbr }: Props) {
  const bothConfirmed = awayLineup && homeLineup;
  const platoonAdv = bothConfirmed ? (() => {
    const awayLeft = awayLineup.platoonComposition.leftBats;
    const homeLeft = homeLineup.platoonComposition.leftBats;
    return { awayLeftBats: awayLeft, homeLeftBats: homeLeft };
  })() : null;

  return (
    <div className="space-y-2">
      <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Lineup Intelligence</div>
      <div className="grid grid-cols-2 gap-4">
        <LineupPanel lineup={awayLineup} teamAbbr={awayTeamAbbr} />
        <LineupPanel lineup={homeLineup} teamAbbr={homeTeamAbbr} />
      </div>
      {platoonAdv && (
        <div className="card p-3 text-xs text-gray-400">
          <span className="text-gray-500">Platoon composition: </span>
          {awayTeamAbbr} {platoonAdv.awayLeftBats}L · {homeTeamAbbr} {platoonAdv.homeLeftBats}L
        </div>
      )}
    </div>
  );
}
